import type { CreatePublishTargetRequest, PublishJobStatus, PublishTrigger } from '@/lib/api';

/** All publish triggers, in display order, with human labels. */
export const PUBLISH_TRIGGERS: ReadonlyArray<{ value: PublishTrigger; label: string; hint: string }> =
  [
    { value: 'manual', label: 'Manual', hint: 'Only when someone clicks Publish.' },
    {
      value: 'on_import_success',
      label: 'On import',
      hint: 'After a file import finishes successfully.',
    },
    {
      value: 'on_translation_change',
      label: 'On translation change',
      hint: 'When a translation is added or edited (coalesced).',
    },
  ];

export type PublishFormat = NonNullable<CreatePublishTargetRequest['format']>;

/** Editable shape backing the create/edit dialog. */
export interface PublishTargetForm {
  name: string;
  credentialId: string;
  bucket: string;
  region: string;
  prefix: string;
  format: PublishFormat;
  /** Selected language codes; empty means "all project target languages". */
  langs: string[];
  triggers: PublishTrigger[];
  enabled: boolean;
}

export function emptyPublishTargetForm(): PublishTargetForm {
  return {
    name: '',
    credentialId: '',
    bucket: '',
    region: '',
    prefix: '',
    format: 'po',
    langs: [],
    triggers: ['manual'],
    enabled: true,
  };
}

/** True when the form has the fields the API requires for create/update. */
export function isPublishFormValid(form: PublishTargetForm): boolean {
  return (
    form.name.trim().length >= 2 &&
    form.credentialId.trim() !== '' &&
    form.bucket.trim() !== '' &&
    form.region.trim() !== ''
  );
}

/** Project the form into the wire request, dropping empty optionals. */
export function buildPublishTargetRequest(
  form: PublishTargetForm,
): CreatePublishTargetRequest {
  const prefix = form.prefix.trim();
  return {
    name: form.name.trim(),
    kind: 's3',
    credentialId: form.credentialId,
    bucket: form.bucket.trim(),
    region: form.region.trim(),
    ...(prefix ? { prefix } : {}),
    format: form.format,
    langs: form.langs,
    triggers: form.triggers,
    enabled: form.enabled,
  };
}

const JOB_TONE: Record<PublishJobStatus, 'muted' | 'primary' | 'positive' | 'negative'> = {
  queued: 'muted',
  running: 'primary',
  succeeded: 'positive',
  failed: 'negative',
};

const JOB_LABEL: Record<PublishJobStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
};

export function jobStatusTone(status: PublishJobStatus) {
  return JOB_TONE[status] ?? 'muted';
}

export function jobStatusLabel(status: PublishJobStatus) {
  return JOB_LABEL[status] ?? status;
}

export function isTerminalStatus(status: PublishJobStatus): boolean {
  return status === 'succeeded' || status === 'failed';
}

/** Compact, human bytes for the run summary (1024-based). */
export function formatBytes(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const rounded = value >= 10 || i === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[i]}`;
}
